import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { COMPANY, NAV } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#050B14]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => go("#top")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="grid place-items-center h-10 w-10 border border-[#FF5A00] text-[#F8FAFC] font-serif-display font-bold relative overflow-hidden">
            <span className="relative z-10">JP</span>
            <Zap className="absolute -bottom-2 -right-1 h-4 w-4 text-[#FF5A00] opacity-70" />
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif-display text-lg tracking-tight">{COMPANY.name}</span>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
              LTD — Offshore
            </span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#F8FAFC] transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[#FF5A00] hover:after:w-full after:transition-[width] after:duration-300"
            >
              {n.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 bg-[#FF5A00] text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#F8FAFC] hover:text-[#050B14] transition-colors duration-300"
          >
            Request a Quote
          </button>
        </div>

        <button
          className="md:hidden text-[#F8FAFC]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-[#050B14]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="text-left font-mono-tech text-sm uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#F8FAFC]"
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="bg-[#FF5A00] text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-3 text-center"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
